import React, { useState, useEffect, useCallback } from 'react';
import {
  getKioskSettings, saveKioskSetting, listPhysicians, listPractices,
  listDevices, mintDevice, revokeDevice, syncPayers,
  listPayers, listCarrierOverrides, addCarrierOverride, deleteCarrierOverride,
} from '../lib/kiosk-admin-api';

// Staff-validated carrier matches: "this card-printed name / payer ID means
// THIS Elation company." Checked before any fuzzy matching by the intake +
// kiosk carrier mapper, and they survive payer re-syncs.
function CarrierOverridesCard() {
  const [overrides, setOverrides] = useState([]);
  const [payers, setPayers] = useState([]);
  const [carrierName, setCarrierName] = useState('');
  const [payerId, setPayerId] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([listCarrierOverrides(), listPayers()]);
      setOverrides(o); setPayers(p); setErr(null);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  async function onAdd() {
    if (busy) return;
    if (!carrierName.trim() && !payerId.trim()) { setErr('Enter the card-printed carrier name and/or payer ID.'); return; }
    if (!target) { setErr('Pick the Elation carrier it should link to.'); return; }
    setBusy(true); setErr(null);
    try {
      await addCarrierOverride({ carrierName: carrierName.trim(), payerId: payerId.trim(), elationCompanyId: target });
      setCarrierName(''); setPayerId(''); setTarget('');
      setOverrides(await listCarrierOverrides());
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function onDelete(id) {
    if (!window.confirm('Remove this carrier match? Future cards with this name/payer ID go back to automatic matching.')) return;
    try { await deleteCarrierOverride(id); setOverrides(await listCarrierOverrides()); }
    catch (e) { setErr(e.message); }
  }

  return (
    <section className="kiosk-card">
      <h3>Carrier match overrides</h3>
      <div className="kiosk-hint">
        When an intake card's payer can't be auto-linked (flagged "payer not matched/suggested"), add the
        name exactly as the card prints it — future cards then link automatically. Overrides survive payer syncs.
      </div>
      <div className="kiosk-add-device">
        <input type="text" placeholder="Carrier name as printed on the card" value={carrierName}
          onChange={(e) => setCarrierName(e.target.value)} maxLength={120} />
        <input type="text" placeholder="Payer ID (optional)" value={payerId}
          onChange={(e) => setPayerId(e.target.value)} maxLength={40} style={{ maxWidth: 160 }} />
        <select value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">— Elation carrier —</option>
          {payers.map((p) => (
            <option key={p.elation_company_id} value={p.elation_company_id}>
              {p.carrier}{p.payer_id ? ` (${p.payer_id})` : ''}
            </option>
          ))}
        </select>
        <button className="btn sm" disabled={busy} onClick={onAdd}>{busy ? 'Adding…' : 'Add match'}</button>
      </div>
      {err && <div className="kiosk-admin-error">Error: {err}</div>}
      <table className="kiosk-device-table">
        <thead><tr><th>Card name</th><th>Payer ID</th><th>Links to</th><th>Added</th><th></th></tr></thead>
        <tbody>
          {overrides.length === 0 && <tr><td colSpan={5} className="kiosk-empty">No overrides yet.</td></tr>}
          {overrides.map((o) => (
            <tr key={o.id}>
              <td>{o.alias_norm || <em>any</em>}</td>
              <td className="tabnum">{o.payer_id_norm || '—'}</td>
              <td>{o.carrier || <em>#{o.elation_company_id} (not in synced list — re-sync payers)</em>}</td>
              <td className="tabnum">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</td>
              <td><button className="btn ghost sm" onClick={() => onDelete(o.id)}>Remove</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function KioskAdminPage({ user }) {
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const [settings, setSettings] = useState({});
  const [physicians, setPhysicians] = useState([]);
  const [practices, setPractices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState(null); // shown once after mint
  const [label, setLabel] = useState('');
  const [minting, setMinting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const [s, ph, pr, dv] = await Promise.all([
        getKioskSettings(),
        listPhysicians().catch(() => []),
        listPractices().catch(() => []),
        listDevices().catch(() => []),
      ]);
      setSettings(s); setPhysicians(ph); setPractices(pr); setDevices(dv); setError(null);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);

  const physicianId = settings.kiosk_primary_physician_id ?? '';
  const practiceId = settings.kiosk_caregiver_practice ?? '';
  const enabled = settings.kiosk_intake_enabled === true;
  const canEnable = physicianId !== '' && practiceId !== '';

  async function save(key, value) {
    if (!isAdmin) { setError('Only an admin can change practice settings.'); return; }
    const prev = settings;
    setSettings({ ...prev, [key]: value });
    setSaving(true); setError(null);
    try { await saveKioskSetting(key, value); }
    catch (e) { setSettings(prev); setError(e.message || 'Could not save setting.'); }
    finally { setSaving(false); }
  }

  async function onMint() {
    if (minting) return;
    if (!label.trim()) { setError('Enter a device label first.'); return; }
    setError(null); setMinting(true);
    try {
      const res = await mintDevice(label.trim());
      setNewKey({ label: label.trim(), key: res.key });
      setLabel('');
      setDevices(await listDevices());
    } catch (e) { setError(e.message); }
    finally { setMinting(false); }
  }

  async function onRevoke(id) {
    if (!window.confirm('Revoke this device key? The kiosk using it will stop working immediately.')) return;
    try { await revokeDevice(id); setDevices(await listDevices()); }
    catch (e) { setError(e.message); }
  }

  async function onSyncPayers() {
    if (syncing) return;
    setSyncing(true); setSyncMsg(''); setError(null);
    try { const r = await syncPayers(); setSyncMsg(`Synced ${r.count} payers.`); }
    catch (e) { setError(e.message); }
    finally { setSyncing(false); }
  }

  if (!isAdmin) return <div className="kiosk-admin-page"><p>Kiosk settings are admin-only.</p></div>;

  return (
    <div className="kiosk-admin-page">
      <h2>Kiosk Intake</h2>
      {error && <div className="kiosk-admin-error">Error: {error}</div>}

      <section className="kiosk-card">
        <h3>Enablement</h3>
        <div className="kiosk-field">
          <label>Default provider for new charts</label>
          <select value={physicianId} disabled={saving}
            onChange={(e) => save('kiosk_primary_physician_id', e.target.value ? Number(e.target.value) : '')}>
            <option value="">— select —</option>
            {physicians.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="kiosk-field">
          <label>Caregiver practice</label>
          <select value={practiceId} disabled={saving}
            onChange={(e) => save('kiosk_caregiver_practice', e.target.value ? Number(e.target.value) : '')}>
            <option value="">— select —</option>
            {practices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="kiosk-field kiosk-toggle-row">
          <label>Enable kiosk intake for this practice</label>
          <input type="checkbox" checked={enabled} disabled={saving || !canEnable}
            onChange={(e) => save('kiosk_intake_enabled', e.target.checked)} />
          {!canEnable && <span className="kiosk-hint">Select a provider and practice first.</span>}
        </div>
        <div className="kiosk-field">
          <button className="btn sm" disabled={syncing} onClick={onSyncPayers}>
            {syncing ? 'Syncing…' : 'Sync payers from Elation'}
          </button>
          {syncMsg && <span className="kiosk-hint">{syncMsg}</span>}
          <div className="kiosk-hint">Run this for the practice before enabling, and after payer changes — it powers carrier matching + eligibility.</div>
        </div>
      </section>

      <section className="kiosk-card">
        <h3>Kiosk devices</h3>
        {newKey && (
          <div className="kiosk-new-key">
            <strong>New device key for "{newKey.label}" — save it now, it will not be shown again:</strong>
            <code className="kiosk-key-box">{newKey.key}</code>
            <button className="btn sm" onClick={() => navigator.clipboard?.writeText(newKey.key)}>Copy</button>
            <button className="btn ghost sm" onClick={() => setNewKey(null)}>Dismiss</button>
          </div>
        )}
        <div className="kiosk-add-device">
          <input type="text" placeholder="Device label (e.g., Front desk iPad)" value={label}
            onChange={(e) => setLabel(e.target.value)} maxLength={100} />
          <button className="btn sm" disabled={minting} onClick={onMint}>{minting ? 'Adding…' : 'Add device'}</button>
        </div>
        <table className="kiosk-device-table">
          <thead><tr><th>Label</th><th>Prefix</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {devices.length === 0 && <tr><td colSpan={4} className="kiosk-empty">No devices yet.</td></tr>}
            {devices.map((d) => (
              <tr key={d.id} className={d.revoked_at ? 'kiosk-revoked' : ''}>
                <td>{d.label}</td>
                <td className="tabnum">{d.key_prefix}…</td>
                <td className="tabnum">{d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}</td>
                <td>{d.revoked_at ? <em>revoked</em> : <button className="btn ghost sm" onClick={() => onRevoke(d.id)}>Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <CarrierOverridesCard />
    </div>
  );
}
